import { Bell, EyeOff, LayoutList, PanelTop } from "lucide-react";

import type { AlertSurface } from "../../alertDisplayPrefs";
import { InfoHint } from "../../components/InfoHint";
import { useWeaponAlertsContext } from "../../components/WeaponAlertsContext";
import { SettingsSection } from "./shared";

const SURFACES: Array<{
  id: AlertSurface;
  label: string;
  hint: string;
  icon: typeof PanelTop;
}> = [
  {
    id: "ribbon",
    label: "Ribbon",
    hint: "Top banner only",
    icon: PanelTop,
  },
  {
    id: "alerts",
    label: "Alerts page",
    hint: "Alerts list only",
    icon: LayoutList,
  },
  {
    id: "off",
    label: "Off",
    hint: "Hidden everywhere",
    icon: EyeOff,
  },
];

function SurfacePicker({
  label,
  description,
  hint,
  value,
  onChange,
}: {
  label: string;
  description: string;
  hint: React.ReactNode;
  value: AlertSurface;
  onChange: (v: AlertSurface) => void;
}) {
  return (
    <div className="bf-card space-y-3">
      <div>
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-white">
          {label}
          <InfoHint>{hint}</InfoHint>
        </h3>
        <p className="mt-0.5 text-xs text-bf-muted">{description}</p>
      </div>
      <div role="radiogroup" aria-label={label} className="grid grid-cols-3 gap-2">
        {SURFACES.map(({ id, label: optionLabel, hint: optionHint, icon: Icon }) => {
          const selected = value === id;
          return (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(id)}
              className={
                "flex flex-col items-start gap-1 rounded-lg px-2.5 py-2.5 text-left transition " +
                (selected
                  ? "bg-brand-purple/15 text-white ring-1 ring-inset ring-brand-purple/40"
                  : "bg-white/[0.03] text-bf-muted ring-1 ring-inset ring-white/10 hover:bg-white/5 hover:text-bf-text")
              }
            >
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold">
                <Icon size={14} className="shrink-0" />
                {optionLabel}
              </span>
              <span className="text-xs leading-snug text-bf-muted">{optionHint}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function AlertsSection() {
  const { displayPrefs, setDisplayPrefs } = useWeaponAlertsContext();

  return (
    <SettingsSection
      title="Alert settings"
      description="Each category appears in one place — the ribbon, the Alerts page — or nowhere."
      hint={
        <>
          These two are exclusive: ribbon, Alerts page, or off. Confirmed
          threats always use both the ribbon and the Alerts page.
        </>
      }
    >
      <div className="grid gap-3 lg:grid-cols-2">
        <SurfacePicker
          label="Verification"
          description="While a detection is still being checked."
          hint="Amber “verifying” state. Ribbon = banner only. Alerts page = Under verification strip only. Off = hidden until it resolves."
          value={displayPrefs.verification}
          onChange={(verification) => setDisplayPrefs({ verification })}
        />
        <SurfacePicker
          label="False alarms"
          description="After a check finds no threat."
          hint="Purple “false alarm” state. Ribbon = banner for newly cleared checks only. Alerts page = False alarms column only. Off = hidden."
          value={displayPrefs.falsePositive}
          onChange={(falsePositive) => setDisplayPrefs({ falsePositive })}
        />
      </div>

      <p className="flex items-start gap-2 text-xs text-bf-muted">
        <Bell size={13} className="mt-0.5 shrink-0" />
        Saved in this browser. Confirmed threats still always appear in the
        ribbon and on the Alerts page.
      </p>
    </SettingsSection>
  );
}
